import { useState, useEffect } from 'react'
import Hero from '../components/Hero'
import ProductCard from '../components/ProductCard'
import ProductModal from '../components/ProductModal'
import EventCard from '../components/EventCard'
import { productsAPI, eventsAPI } from '../services/api'
import { ArrowRight, Leaf, TrendingUp, Users } from 'lucide-react'

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([])
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [productsResponse, eventsResponse] = await Promise.all([
          productsAPI.getFeatured(3),
          eventsAPI.getUpcoming(3)
        ])

        setFeaturedProducts(productsResponse.data.data)
        setUpcomingEvents(eventsResponse.data.data)
      } catch (err) {
        console.error('Error fetching data:', err)
        setError('Failed to load content. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const stats = [
    { icon: Leaf, value: '500+', label: 'Products Available' },
    { icon: TrendingUp, value: '25%', label: 'Average Yield Increase' },
    { icon: Users, value: '10K+', label: 'Happy Farmers' },
  ]

  const handleProductClick = (product) => {
    setSelectedProduct(product)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setTimeout(() => setSelectedProduct(null), 300)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-earth-green"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <Hero />

      {/* Stats Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon
              return (
                <div key={index} className="text-center">
                  <div className="bg-brand-blue p-4 rounded-full w-fit mx-auto mb-4">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-brand-blue mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4">Featured Products</h2>
            <p className="subheading">
              Discover our most popular fertilizers and plant nutrients,
              carefully formulated for optimal agricultural results.
            </p>
          </div>

          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} onProductClick={handleProductClick} />
              ))}
            </div>
          )}

          <div className="text-center">
            <a
              href="/products"
              className="btn-primary inline-flex items-center group"
            >
              View All Products
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4">Upcoming Events</h2>
            <p className="subheading">
              Join our workshops, seminars, and field days to learn about
              the latest in sustainable agriculture and farming techniques.
            </p>
          </div>

          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {upcomingEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}

          <div className="text-center">
            <a
              href="/events"
              className="btn-secondary inline-flex items-center group"
            >
              View All Events
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding hero-gradient">
        <div className="container-custom text-center text-white">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Grow Better Yields?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-200">
            Join thousands of farmers who trust Novatarra for their agricultural needs.
            Get expert advice and premium products for sustainable farming success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/products"
              className="bg-white text-earth-green hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl inline-flex items-center justify-center"
            >
              Show Products
            </a>
            <a
              href="/about#contact"
              className="border-2 border-white text-white hover:bg-white hover:text-earth-green font-semibold py-3 px-8 rounded-lg transition-all duration-200 inline-flex items-center justify-center"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  )
}

export default Home
